import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Card, CardContent } from '@Components/Card';
import VenueQuickFilter from '@Components/CustomDataTableFilters/VenueQuickFilter';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import {
  EventArchivedBadge,
  EventStatusBadge,
  EventVisibilityBadge,
} from '@Components/TicketedEvents/Badges';
import { useTicketedEvents } from '@Hooks/useTicketedEvents';
import type { TicketedEvent } from '@Types/ticketedEvents';
import {
  buildFilterParams,
  formatZonedDateTime,
  getStatusSortKey,
} from '@Utils/utils';
import { Ticket } from 'lucide-react';
import { useQueryStateFixed } from '@Utils/nuqs-fix';
import { parseAsBoolean } from 'nuqs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';

const NOT_AVAILABLE = 'N/A';

// Helper function to calculate tickets sold per ticket type
function getTicketsSoldByType(event: TicketedEvent): Map<string, number> {
  const counts = new Map<string, number>();
  if (event.tickets && Array.isArray(event.tickets)) {
    event.tickets.forEach((ticket) => {
      // Count tickets that have been purchased (have a purchaseId)
      if (ticket.purchaseId && ticket.ticketTypeId) {
        const current = counts.get(ticket.ticketTypeId) || 0;
        counts.set(ticket.ticketTypeId, current + 1);
      }
    });
  }
  return counts;
}

// Helper function to format ticket sales display
function formatTicketSales(event: TicketedEvent): string {
  if (!event.ticketTypes || event.ticketTypes.length === 0) {
    return 'No ticket types';
  }

  const soldCounts = getTicketsSoldByType(event);

  return event.ticketTypes
    .map((ticketType) => {
      const sold = soldCounts.get(ticketType.id) || 0;
      const available = ticketType.available || 0;
      return `${ticketType.name}: ${sold}/${available}`;
    })
    .join(', ');
}

// ---------- column configs ----------

const ticketedEventColumnConfigs: ColumnConfig<TicketedEvent>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? NOT_AVAILABLE,
    type: 'text',
    size: 260,
    cell: (info) => {
      const event = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <span>{event.name ?? NOT_AVAILABLE}</span>
          <EventArchivedBadge event={event} />
        </div>
      );
    },
  },
  {
    id: 'venueId',
    headerLabel: 'Venue',
    accessor: (row) => row.venue?.name ?? NOT_AVAILABLE,
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'ticketSales',
    headerLabel: 'Ticket Sales',
    accessor: (row) => formatTicketSales(row),
    type: 'text',
    enableSorting: false,
    cell: (info) => {
      const event = info.row.original;
      if (!event.ticketTypes || event.ticketTypes.length === 0) {
        return <span className="text-zinc-500">No ticket types</span>;
      }

      const soldCounts = getTicketsSoldByType(event);

      return (
        <div className="flex flex-col gap-1">
          {event.ticketTypes.map((ticketType) => {
            const sold = soldCounts.get(ticketType.id) || 0;
            const available = ticketType.available || 0;

            return (
              <div key={ticketType.id}>
                <div className={'mr-2'}>
                  {sold}/{available} sold
                </div>
                <div className="flex items-center gap-1 text-xs font-medium text-zinc-600">
                  <Ticket size={12} /> {ticketType.name}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
  },
  {
    id: 'capacity',
    headerLabel: 'Capacity',
    accessor: (row) => row.capacity ?? NOT_AVAILABLE,
    type: 'number',
  },
  {
    id: 'startDateTime',
    headerLabel: 'Start Date',
    accessor: (row) => row.startDateTime ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return NOT_AVAILABLE;
      const dateString = formatZonedDateTime(
        date,
        "MMMM dd, yyyy 'at' hh:mm a",
      );
      return dateString || NOT_AVAILABLE;
    },
  },
  {
    id: 'endDateTime',
    headerLabel: 'End Date',
    accessor: (row) => row.endDateTime ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return NOT_AVAILABLE;
      const dateString = formatZonedDateTime(
        date,
        "MMMM dd, yyyy 'at' hh:mm a",
      );
      return dateString || NOT_AVAILABLE;
    },
  },
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => row.status ?? NOT_AVAILABLE,
    type: 'text',
    customSortingFn: (rowA, rowB) => {
      const keyA = getStatusSortKey(rowA.original);
      const keyB = getStatusSortKey(rowB.original);
      return keyA - keyB;
    },
    cell: (info) => {
      const event = info.row.original;
      return <EventStatusBadge event={event} />;
    },
  },
  {
    id: 'visibility',
    headerLabel: 'Visibility',
    accessor: (row) => row.visibility ?? NOT_AVAILABLE,
    type: 'text',
    cell: (info) => {
      const event = info.row.original;
      return <EventVisibilityBadge event={event} />;
    },
  },
];

export default function TicketedEventsList() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useQueryStateFixed<boolean>(
    'archived',
    parseAsBoolean.withDefault(false),
  );

  const controller = useResourceTableController<TicketedEvent>({
    columnsConfig: ticketedEventColumnConfigs,
    defaultSort: {
      id: 'startDateTime',
      desc: true,
    },
    baseSearchParams: () => ({
      include: 'venue,tickets,ticketTypes',
      ...(showArchived ? {} : { 'filter[archivedAt]': 'null' }),
    }),
    buildFilterParams: (columnFilters) => buildFilterParams(columnFilters),
    quickFilterFields: [{ field: 'name', label: 'Name' }],
    initialPageSize: 10,
    initialPinning: {
      left: ['name'],
      right: [],
    },
  });

  const { data: payload, status } = useTicketedEvents({
    searchParams: controller.searchParams,
  });

  const { data: eventsMeta, status: eventsMetaStatus } = useTicketedEvents({
    searchParams: {
      fields: 'id',
    },
  });

  const rows = useMemo(() => {
    const rawRows: TicketedEvent[] = Array.isArray(payload)
      ? payload
      : (payload?.data ?? []);

    const sortConfig = controller.serverSorting?.find((s) => s.id === 'status');
    if (!sortConfig) return rawRows;

    return [...rawRows].sort((a, b) => {
      const keyA = getStatusSortKey(a);
      const keyB = getStatusSortKey(b);
      const diff = keyA - keyB;
      return sortConfig.desc ? -diff : diff;
    });
  }, [payload, controller.serverSorting]);

  const meta = useMemo(() => {
    if (eventsMetaStatus !== 'success') return undefined;
    if (!Array.isArray(eventsMeta)) return undefined;
    return {
      total: Array.isArray(eventsMeta) ? eventsMeta.length : 0,
    };
  }, [eventsMeta, eventsMetaStatus]);

  return (
    <Card>
      <CardContent>
        <ResourceTableView<TicketedEvent>
          label="Ticketed Events"
          controller={controller}
          data={rows}
          meta={meta}
          status={status}
          onRowClick={(row) => navigate(`/ticketed-events/${row.id}`)}
          renderQuickFiltersExtra={({ table }) => (
            <>
              <VenueQuickFilter<TicketedEvent> table={table} />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-archived"
                  checked={showArchived ?? false}
                  onCheckedChange={(checked) =>
                    setShowArchived(checked === true)
                  }
                />
                <Label htmlFor="show-archived">Show archived</Label>
              </div>
            </>
          )}
        />
      </CardContent>
    </Card>
  );
}
